/**
 * Quantity break selector ("Buy 2 save 5%" ...).
 * Clicking a tier writes the quantity into the product form's quantity input and keeps the
 * highlighted tier in sync when the shopper uses the +/- buttons or types a number.
 */
const OPTION_SELECTOR = '[data-qty-break]';

/** @param {Element} root */
function getQuantityInput(root) {
  const form = root.closest('form');
  const input = form?.querySelector('input[name="quantity"]');
  return input instanceof HTMLInputElement ? input : null;
}

/** @param {Element} root */
function sync(root) {
  const input = getQuantityInput(root);
  if (!input) return;
  const current = parseInt(input.value, 10) || 1;
  let matched = false;
  root.querySelectorAll(OPTION_SELECTOR).forEach((option) => {
    const isMatch = Number(option.getAttribute('data-qty-break')) === current;
    option.setAttribute('aria-checked', isMatch ? 'true' : 'false');
    if (isMatch) matched = true;
  });
  // A custom quantity (e.g. 3) matches no tier: nothing is highlighted.
  root.toggleAttribute('data-custom-quantity', !matched);
}

function syncAll() {
  document.querySelectorAll('[data-qty-breaks]').forEach(sync);
}

if (!window.__qtyBreaksInit) {
  window.__qtyBreaksInit = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const option = target?.closest(OPTION_SELECTOR);
    if (!option) return;
    const root = option.closest('[data-qty-breaks]');
    if (!root) return;
    const input = getQuantityInput(root);
    if (!input || input.disabled) return;

    input.value = option.getAttribute('data-qty-break') || '1';
    // Lets the theme's quantity component clamp the value and refresh its own state.
    input.dispatchEvent(new Event('blur'));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    sync(root);
  });

  document.addEventListener('quantity-selector:update', () => requestAnimationFrame(syncAll));
  document.addEventListener('input', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') syncAll();
  });
  document.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') syncAll();
  });
}


/* ---- Prices follow the selected variant ---------------------------------------------------- */

/** Formats cents with the shop's money format (e.g. "${{amount}}"). */
function formatCents(cents, format) {
  const fmt = format || '${{amount}}';
  const value = Number(cents) || 0;
  const withDelimiters = (number, decimals, thousands, decimal) => {
    const [whole, frac] = (number / 100).toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? grouped + decimal + frac : grouped;
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withDelimiters(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(value, 2, "'", '.');
      default:
        return withDelimiters(value, 2, ',', '.');
    }
  });
}

/** Recomputes every tier from a unit price in cents (same rounding as snippets/quantity-breaks.liquid). */
function repriceTiers(root, unitCents) {
  const format = root.getAttribute('data-money-format') || '';
  root.querySelectorAll(OPTION_SELECTOR).forEach((option) => {
    const qty = Number(option.getAttribute('data-qty-break')) || 1;
    const pct = Number(option.getAttribute('data-qty-pct')) || 0;
    const was = unitCents * qty;
    const total = Math.round((was * (100 - pct)) / 100);
    const each = Math.round(total / qty);
    const totalEl = option.querySelector('.qty-breaks__total');
    const wasEl = option.querySelector('.qty-breaks__was');
    const eachEl = option.querySelector('.qty-breaks__each');
    if (totalEl) totalEl.textContent = formatCents(qty === 1 ? unitCents : total, format);
    if (wasEl) wasEl.textContent = formatCents(was, format);
    if (eachEl) eachEl.textContent = `${formatCents(each, format)} each`;
  });
}

/** @param {Event & {detail?: any}} event */
function onVariantUpdate(event) {
  const detail = event.detail || {};
  const variant = detail.resource;
  const productId = String(detail.data?.productId || '');
  const html = detail.data?.html;
  document.querySelectorAll('[data-qty-breaks]').forEach((root) => {
    if (productId && root.getAttribute('data-product-id') && root.getAttribute('data-product-id') !== productId) return;

    // 1. The server-rendered table for the new variant, when the response carries it.
    const fresh =
      html instanceof Document || html instanceof DocumentFragment || html instanceof Element
        ? html.querySelector(`[data-qty-breaks][data-product-id="${root.getAttribute('data-product-id')}"]`) ||
          html.querySelector('[data-qty-breaks]')
        : null;
    const freshList = fresh?.querySelector('.qty-breaks__list');
    const list = root.querySelector('.qty-breaks__list');
    if (freshList && list) {
      list.replaceWith(freshList.cloneNode(true));
      root.setAttribute('data-variant-id', fresh.getAttribute('data-variant-id') || '');
      root.hidden = false;
      sync(root);
      return;
    }

    // 2. Otherwise recompute from the variant's price.
    if (!variant) return;
    if (variant.available === false) {
      root.hidden = true;
      return;
    }
    root.hidden = false;
    if (variant.id) root.setAttribute('data-variant-id', String(variant.id));
    if (variant.price != null) repriceTiers(root, Number(variant.price));
    sync(root);
  });
}

if (!window.__qtyBreaksVariantInit) {
  window.__qtyBreaksVariantInit = true;
  document.addEventListener('variant:update', onVariantUpdate);
}

class QtyBreaks extends HTMLElement {
  connectedCallback() {
    sync(this);
    // The form is morphed after a variant change; re-sync once it has settled.
    requestAnimationFrame(() => sync(this));
  }
}

if (!customElements.get('qty-breaks')) {
  customElements.define('qty-breaks', QtyBreaks);
}

syncAll();
